// Answer-HTML helpers for the panel. The answer HTML comes from the model /
// retrieved web content, so it is untrusted; it is also injected via innerHTML.
// These helpers run WIDGET-SIDE ONLY — the TryProtege peer is shared with the
// ask-protege-page (same conversation endpoint), so anything done here can't
// affect that page.

// Elements that can execute script or fetch remote resources are removed outright.
const FORBIDDEN_SELECTOR =
  "script, style, iframe, object, embed, img, svg, link, meta, base, form, video, audio, source, track";

/**
 * Returns a cleaned copy of `html` for safe injection: anchors unwrapped to their
 * (formatted) text so links aren't clickable, script/media/remote-loading elements
 * removed, and inline event handlers stripped.
 */
export function scrubAnswerHtml(html) {
  if (!html) return "";

  // A <template>'s content is an inert document fragment: assigning innerHTML
  // parses the markup WITHOUT running scripts or loading images, so nothing
  // dangerous fires while we clean it.
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const frag = tpl.content;

  frag.querySelectorAll(FORBIDDEN_SELECTOR).forEach((el) => el.remove());

  // Unwrap anchors: keep their (formatted) contents in place but drop the <a>
  // wrapper, so the text still renders but is not clickable.
  frag.querySelectorAll("a").forEach((anchor) => {
    const parent = anchor.parentNode;
    if (!parent) return;
    while (anchor.firstChild) parent.insertBefore(anchor.firstChild, anchor);
    parent.removeChild(anchor);
  });

  // Strip any inline event handlers (onclick, onerror, …) left on survivors.
  frag.querySelectorAll("*").forEach((el) => {
    for (const attr of [...el.attributes]) {
      if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
    }
  });

  return tpl.innerHTML;
}

/**
 * Returns the text of every <strong>/<b> in `html` — i.e. the exact heading/label
 * phrases the upstream FINAL answer chose to bold. The streamed Markdown deltas
 * don't contain this bold (the model sends plain text; the upstream's final
 * renderer adds it), so we read the real list here rather than guessing it.
 */
export function extractBoldPhrases(html) {
  if (!html) return [];
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const phrases = [];
  tpl.content.querySelectorAll("strong, b").forEach((el) => {
    const text = (el.textContent || "").trim();
    if (text) phrases.push(text);
  });
  return phrases;
}

/**
 * Bolds each phrase in `phrases` where it appears as a lead-in label at the very
 * start of a <p>/<li> (immediately followed by a colon) inside `container`, IN
 * PLACE. Using the upstream final's exact phrases makes the streamed answer end up
 * looking like the final (same headings bold, any format — case names, titles,
 * definitions) without swapping the whole body in (which would re-render it — the
 * "reload"). Only the matched heading text changes; everything else is untouched.
 */
export function applyBoldPhrases(container, phrases) {
  if (!container || !phrases || !phrases.length) return;
  const unique = [...new Set(phrases.map((p) => p.trim()).filter(Boolean))];

  container.querySelectorAll("p, li").forEach((el) => {
    const first = el.firstChild;
    if (!first || first.nodeType !== 3) return; // must begin with a text node

    const text = first.nodeValue;
    const leadLen = text.length - text.trimStart().length; // leading whitespace
    const body = text.slice(leadLen);

    for (const phrase of unique) {
      if (body.startsWith(phrase) && body.charAt(phrase.length) === ":") {
        const rest = text.slice(leadLen + phrase.length); // ":<ws>…"
        const strong = document.createElement("strong");
        strong.textContent = phrase;

        const replacement = document.createDocumentFragment();
        if (leadLen) replacement.appendChild(document.createTextNode(text.slice(0, leadLen)));
        replacement.appendChild(strong);
        replacement.appendChild(document.createTextNode(rest));
        el.replaceChild(replacement, first);
        break;
      }
    }
  });
}
